"use client";

import React from "react";
import { Scale, FileCheck2, Building2, Wheat, ArrowRight, ShieldAlert, ChevronRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
interface PracticeItem {
  id: string;
  title: string;
  badge: string;
  description: string;
  focus: string;
  bullet1: string;
  bullet2: string;
  bullet3: string;
}
const practiceAreas: PracticeItem[] = [{
  id: "administrativo",
  title: "Derecho Administrativo",
  badge: "Sector Público y Regulados",
  description: "Defensa integral de particulares y empresas frente a actos de autoridad administrativa en los tres órdenes de gobierno.",
  focus: "Litigio contencioso y recursos de revocación",
  bullet1: "Juicios de nulidad ante el TFJA y tribunales locales",
  bullet2: "Atención de visitas domiciliarias e inspecciones",
  bullet3: "Impugnación de sanciones y cancelaciones registrales"
}, {
  id: "fiscal",
  title: "Derecho Fiscal",
  badge: "Estrategia Tributaria",
  description: "Acompañamiento preventivo y contencioso en materia impositiva para salvaguardar la viabilidad financiera de su organización.",
  focus: "Acuerdos conclusivos ante PRODECON",
  bullet1: "Defensa contra créditos fiscales y embargos",
  bullet2: "Atención de auditorías del SAT y secretarías estatales",
  bullet3: "Juicios de amparo en materia tributaria"
}, {
  id: "corporativo",
  title: "Derecho Corporativo",
  badge: "Estructura y Gobernanza",
  description: "Instrumentación jurídica y orden operativo para sociedades mercantiles, grupos empresariales y contratos comerciales.",
  focus: "Gobierno corporativo y acuerdos entre socios",
  bullet1: "Protocolización de asambleas ordinarias y extraordinarias",
  bullet2: "Fusiones, escisiones y reorganizaciones societarias",
  bullet3: "Diseño y blindaje de contratos mercantiles estratégicos"
}, {
  id: "agrario",
  title: "Derecho Agrario",
  badge: "Tenencia de la Tierra",
  description: "Seguridad jurídica en la tenencia, aprovechamiento y regularización de predios rústicos, ejidales y comunales en México.",
  focus: "Asambleas ejidales y contratos de usufructo",
  bullet1: "Regularización de derechos agrarios y títulos",
  bullet2: "Defensa en Tribunales Unitarios Agrarios (TUA)",
  bullet3: "Negociación y contratos para proyectos productivos"
}];
export default function Legal() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const getIcon = (id: string) => {
    switch (id) {
      case "administrativo":
        return <Scale className="size-5 text-foreground" />;
      case "fiscal":
        return <FileCheck2 className="size-5 text-foreground" />;
      case "corporativo":
        return <Building2 className="size-5 text-foreground" />;
      case "agrario":
        return <Wheat className="size-5 text-foreground" />;
      default:
        return <Scale className="size-5 text-foreground" />;
    }
  };
  return <section id="áreas-y-servicios-jurídicos" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        {/* Section Header: Briefing & Document Sealing Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center mb-16 lg:mb-24">
          <div className="lg:col-span-6 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-muted/60 border border-ring/40 rounded-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-ring" />
              <span className="font-serif text-xs font-bold uppercase tracking-[0.15em] text-foreground">
                Práctica Jurídica Institucional
              </span>
            </div>

            <div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold uppercase tracking-[0.06em] text-foreground leading-[1.15]">
                Jurídico
              </h2>
              {/* ASV Gold Hairline */}
              <div className="mt-4 h-[1.5px] w-12 bg-ring" />
            </div>

            <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed">
              Brindamos certeza y defensa legal de alto rigor institucional para
              empresas, órganos públicos y personas físicas. Diseñamos
              estrategias preventivas y contenciosas alineadas al marco
              normativo mexicano, con supervisión técnica de especialistas senior.
            </p>

            <div className="pt-2 flex flex-wrap items-center gap-4">
              <Button asChild className="bg-primary text-primary-foreground border border-ring hover:bg-ring hover:text-foreground transition-colors uppercase tracking-[0.08em] font-serif font-semibold text-xs h-11 px-6 rounded-sm shadow-xs">
                <Link to="/ContactPage">Solicitar asesoría legal</Link>
              </Button>

              <Button asChild variant="outline" className="border-foreground/25 text-foreground hover:bg-muted font-serif uppercase tracking-[0.08em] font-semibold text-xs h-11 px-5 rounded-sm">
                <Link to="/OnlineConsultingPage">
                  Consultoría en línea
                  <ChevronRight className="size-4 ml-1 text-ring" />
                </Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="relative border border-foreground/10 p-2 sm:p-3 bg-card rounded-sm shadow-sm">
              <div className="relative overflow-hidden rounded-sm aspect-[16/10]">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/mexican_corporate_attorneys_signing_contract.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/mexican_corporate_attorneys_signing_contract.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/mexican_corporate_attorneys_signing_contract.webp-wvc-sizes" alt="Abogados corporativos mexicanos durante firma protocolaria de contratos y revisión de expedientes" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-primary/20 pointer-events-none" />
              </div>

              {/* Decorative institutional verification badge */}
              <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground font-sans px-1">
                <span className="flex items-center gap-2">
                  <ShieldAlert className="size-3.5 text-ring shrink-0" />
                  Estrategia · Cumplimiento · Resultados
                </span>
                <span className="font-serif tracking-wider uppercase text-[11px] font-semibold text-foreground">
                  ASV Consultores
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 4-Column Practice Area Dossiers */}
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between border-b border-border pb-4 gap-4">
            <div>
              <p className="font-serif text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
                Especialidades de litigio y asesoría
              </p>
              <h3 className="font-serif text-xl sm:text-2xl font-semibold uppercase tracking-[0.04em] text-foreground mt-1">
                Áreas de Práctica Especializada
              </h3>
            </div>
            <p className="text-sm text-muted-foreground font-sans max-w-md">
              Atención directa en Sinaloa y representación ante tribunales
              estatales y federales en el país.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {practiceAreas.map((item, index) => <motion.div key={item.id} variants={itemVariants} data-index={index}>
                <Card className="h-full bg-card border border-border rounded-sm py-6 relative flex flex-col justify-between transition-[border-color,box-shadow] duration-300 hover:border-ring/60 hover:shadow-md">
                  {/* Razor-thin gold hairline top accent */}
                  <div className="absolute top-0 inset-x-0 h-[2px] bg-ring" />

                  <CardHeader className="space-y-3 px-6 pb-2">
                    <div className="flex items-center justify-between">
                      <div className="size-10 rounded-sm border border-ring/40 bg-background flex items-center justify-center">
                        {getIcon(item.id)}
                      </div>
                      <span className="font-serif text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground bg-muted/80 px-2 py-0.5 rounded-sm border border-border">
                        {item.badge}
                      </span>
                    </div>

                    <CardTitle className="font-serif text-lg font-semibold uppercase tracking-[0.03em] text-foreground pt-1">
                      {item.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="px-6 pt-2 space-y-4 flex-1 flex flex-col justify-between">
                    <p className="font-sans text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>

                    <div className="pt-3 border-t border-border/70 space-y-2">
                      <p className="font-serif text-[11px] font-semibold tracking-wider uppercase text-foreground">
                        Alcance prioritario:
                      </p>
                      <ul className="space-y-1.5 text-xs text-muted-foreground font-sans">
                        <li className="flex items-start gap-2">
                          <span className="size-1 rounded-full bg-ring mt-1.5 shrink-0" />
                          <span>{item.bullet1}</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="size-1 rounded-full bg-ring mt-1.5 shrink-0" />
                          <span>{item.bullet2}</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="size-1 rounded-full bg-ring mt-1.5 shrink-0" />
                          <span>{item.bullet3}</span>
                        </li>
                      </ul>
                    </div>

                    <div className="pt-4 mt-auto">
                      <Button asChild variant="ghost" className="w-full justify-between p-0 h-auto font-serif text-xs uppercase tracking-[0.08em] font-semibold text-foreground hover:bg-transparent hover:text-foreground group">
                        <Link to="/ContactPage" className="inline-flex items-center justify-between w-full">
                          <span>Consultar expediente</span>
                          <ArrowRight className="size-3.5 text-ring transition-transform group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </motion.div>
        </div>

        {/* Institutional Consultation Banner */}
        <div className="mt-14 p-6 sm:p-8 bg-muted/40 border border-border rounded-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-1 max-w-2xl">
            <h4 className="font-serif text-base sm:text-lg font-semibold uppercase tracking-[0.03em] text-foreground">
              ¿Requiere diagnóstico preliminar o atención de plazo legal?
            </h4>
            <p className="font-sans text-sm text-muted-foreground">
              Nuestro equipo evalúa notificaciones, términos procesales y
              documentación contractual con confidencialidad absoluta.
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0 w-full sm:w-auto">
            <Button asChild className="bg-primary text-primary-foreground border border-ring hover:bg-ring hover:text-foreground font-serif uppercase tracking-[0.08em] font-semibold text-xs h-10 px-5 rounded-sm w-full sm:w-auto">
              <Link to="/ContactPage">Agendar cita legal</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}